/**
 * Cookie consent state management, geo detection, and dynamic script loader.
 *
 * Reads the `<meta name="a7y-consent-required">` tag injected by the
 * Cloudflare Worker to determine whether the visitor is in a regulated
 * region (EU/EEA, UK, California, Brazil, South Africa, Canada).
 *
 * Consent state is persisted in localStorage under `a7y-cookie-consent`.
 */

import { lsGet, lsPut, lsRemoveByPrefix } from "./local_storage";
import { clearAffiliateState } from "./affiliate_tracking";
import { clearReferralOffer } from "./referral_offer";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConsentCategory = "analytics" | "marketing";

export interface ConsentState {
  /** Whether each non-essential category is accepted. */
  analytics: boolean;
  marketing: boolean;
  /** ISO timestamp of when the user last interacted with the banner. */
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CONSENT_KEY = "a7y-cookie-consent";

const MIXPANEL_TOKEN = "4c0cc0c945351969dec7dd99b345edce";

/**
 * Local development loads no third-party analytics at all (TPF, 2026-09-04).
 *
 * The token above identifies the **production** Mixpanel project, and
 * nothing gated them by host, so every local dev session wrote real pageviews,
 * autocaptured events and 100%-sampled session replays into the live
 * analytics. The internal-account suppression in `mixpanel.ts`
 * (`isInternalAnalyticsEmail`) does not cover any of that: it guards explicit
 * `mpTrack`/`mpIdentify` calls, while autocapture and session recording are
 * configured inside `mixpanel.init()` and start before any identify runs.
 *
 * `import.meta.env.DEV` is the signal rather than `VITE_ENVIRONMENT` because
 * `.env.development` is gitignored — a fresh checkout has no such file, so
 * the variable is undefined there while `DEV` is set by Vite itself and is
 * true for `vite dev` and false for every build (including the SSR/prerender
 * pass, which runs with NODE_ENV=production). The explicit `VITE_ENVIRONMENT`
 * check keeps the gate honest for a dev-flavoured build.
 *
 * Staging deliberately still reports, so analytics wiring can be verified
 * before a production deploy.
 */
const ANALYTICS_SUPPRESSED_IN_DEV =
  import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === "development";

// ---------------------------------------------------------------------------
// Geo detection
// ---------------------------------------------------------------------------

/**
 * Returns true if the visitor is in a regulated region that requires
 * explicit cookie consent. Reads the meta tag injected by the Worker.
 * Defaults to `true` (show banner) if the meta tag is missing.
 */
export function isConsentRequired(): boolean {
  if (typeof document === "undefined") return true;
  const meta = document.querySelector('meta[name="a7y-consent-required"]');
  if (!meta) return true; // fail-safe: show banner
  return meta.getAttribute("content") !== "false";
}

// ---------------------------------------------------------------------------
// Consent state persistence
// ---------------------------------------------------------------------------

/** Read persisted consent state, or null if the user hasn't decided yet. */
export function getConsentState(): ConsentState | null {
  return lsGet(CONSENT_KEY, null);
}

/** Persist consent state to localStorage. */
export function setConsentState(state: ConsentState): void {
  lsPut(CONSENT_KEY, state);
}

// ---------------------------------------------------------------------------
// Consent-change subscribers (A7Y-1895)
// ---------------------------------------------------------------------------

type ConsentChangeListener = (state: ConsentState) => void;

/** Subscribers notified whenever the visitor makes a consent decision. */
const consentChangeListeners = new Set<ConsentChangeListener>();

/**
 * Subscribe to consent-state changes. Called after acceptAll / savePreferences
 * / rejectAll persist and (de)inject scripts. Returns an unsubscribe fn.
 *
 * Used by the app entry points to kick off first-party affiliate tracking the
 * moment marketing consent is granted (see `affiliate_tracking.ts`).
 */
export function onConsentChange(listener: ConsentChangeListener): () => void {
  consentChangeListeners.add(listener);
  return () => {
    consentChangeListeners.delete(listener);
  };
}

/** Notify all subscribers of the latest consent state. */
function notify(state: ConsentState): void {
  for (const listener of consentChangeListeners) {
    try {
      listener(state);
    } catch (err) {
      console.error("consent change listener failed", err);
    }
  }
}

/** Accept all consent categories. */
export function acceptAll(): ConsentState {
  const state: ConsentState = {
    analytics: true,
    marketing: true,
    updatedAt: new Date().toISOString(),
  };
  setConsentState(state);
  loadConsentedScripts(state);
  notify(state);
  return state;
}

/** Reject all non-essential consent categories. */
export function rejectAll(): ConsentState {
  const state: ConsentState = {
    analytics: false,
    marketing: false,
    updatedAt: new Date().toISOString(),
  };
  setConsentState(state);
  clearRevokedData(state);
  notify(state);
  return state;
}

/** Save custom preferences. */
export function savePreferences(
  analytics: boolean,
  marketing: boolean,
): ConsentState {
  const state: ConsentState = {
    analytics,
    marketing,
    updatedAt: new Date().toISOString(),
  };
  setConsentState(state);
  loadConsentedScripts(state);
  clearRevokedData(state);
  notify(state);
  return state;
}

// ---------------------------------------------------------------------------
// Consent checks (called by analytics wrappers)
// ---------------------------------------------------------------------------

/** Returns true if analytics tracking is allowed. */
export function hasAnalyticsConsent(): boolean {
  if (!isConsentRequired()) return true;
  return getConsentState()?.analytics === true;
}

/** Returns true if marketing/affiliate tracking is allowed. */
export function hasMarketingConsent(): boolean {
  if (!isConsentRequired()) return true;
  return getConsentState()?.marketing === true;
}

// ---------------------------------------------------------------------------
// Dynamic script loader
// ---------------------------------------------------------------------------

/** Track which scripts have already been injected to avoid duplicates. */
const injectedScripts = new Set<string>();

function injectScript(
  id: string,
  src?: string,
  attrs?: Record<string, string>,
  inlineCode?: string,
): void {
  if (injectedScripts.has(id)) return;
  injectedScripts.add(id);

  const script = document.createElement("script");
  if (src) {
    script.src = src;
    script.async = true;
  }
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      script.setAttribute(key, value);
    }
  }
  if (inlineCode) {
    script.textContent = inlineCode;
  }
  document.head.appendChild(script);
}

/** Load Mixpanel scripts. */
function loadMixpanel(): void {
  // Mixpanel stub + CDN loader (minified version from original index.html)
  injectScript(
    "mp-init",
    undefined,
    undefined,
    `(function(e,c){if(!c.__SV){var l,h;window.mixpanel=c;c._i=[];c.init=function(q,r,f){function t(d,a){var g=a.split(".");2==g.length&&(d=d[g[0]],a=g[1]);d[a]=function(){d.push([a].concat(Array.prototype.slice.call(arguments,0)))}}var b=c;"undefined"!==typeof f?b=c[f]=[]:f="mixpanel";b.people=b.people||[];b.toString=function(d){var a="mixpanel";"mixpanel"!==f&&(a+="."+f);d||(a+=" (stub)");return a};b.people.toString=function(){return b.toString(1)+".people (stub)"};l="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders start_session_recording stop_session_recording people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");for(h=0;h<l.length;h++)t(b,l[h]);var n="set set_once union unset remove delete".split(" ");b.get_group=function(){function d(p){a[p]=function(){b.push([g,[p].concat(Array.prototype.slice.call(arguments,0))])}}for(var a={},g=["get_group"].concat(Array.prototype.slice.call(arguments,0)),m=0;m<n.length;m++)d(n[m]);return a};c._i.push([q,r,f])};c.__SV=1.2;var k=e.createElement("script");k.type="text/javascript";k.async=!0;k.src="https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";e=e.getElementsByTagName("script")[0];e.parentNode.insertBefore(k,e)}})(document,window.mixpanel||[]);mixpanel.init('${MIXPANEL_TOKEN}',{autocapture:true,record_sessions_percent:100,api_host:'https://api-eu.mixpanel.com',persistence:'localStorage'});`,
  );
}

/**
 * Dynamically inject third-party scripts based on the user's consent.
 * Called after the user accepts (or on page load if consent was previously given).
 *
 * Marketing consent no longer injects any third-party script: affiliate
 * attribution is handled entirely by our first-party tracking (A7Y-1895),
 * which the app entry points fire via `onConsentChange` the moment marketing
 * consent is granted.
 */
export function loadConsentedScripts(state: ConsentState): void {
  clearRetiredGoogleData();
  // One choke point for every caller (initConsent, acceptAll, onConsentChange),
  // so the dev gate cannot be bypassed by a new consent path.
  if (ANALYTICS_SUPPRESSED_IN_DEV) return;
  if (state.analytics) {
    loadMixpanel();
    // The CDN stub queues these calls too if consent changes before it loads.
    window.mixpanel?.opt_in_tracking?.({ track: false });
    // Opt-in resumes events but not a recorder stopped by revocation.
    window.mixpanel?.start_session_recording?.();
  }
}

// ---------------------------------------------------------------------------
// Data cleanup on revocation
// ---------------------------------------------------------------------------

/** Delete cookies by name pattern. */
function deleteCookies(pattern: RegExp): void {
  if (typeof document === "undefined") return;
  document.cookie
    .split(";")
    .map((c) => c.trim().split("=")[0])
    .filter((name) => pattern.test(name))
    .forEach((name) => {
      // GA used the parent domain (.apptonomy.ai), not just www.apptonomy.ai.
      const labels = window.location.hostname.split(".");
      const domains = labels.map((_, index) => labels.slice(index).join("."));
      for (const domain of domains) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });
}

/** Retired first-party Google identifiers are removed regardless of consent. */
function clearRetiredGoogleData(): void {
  deleteCookies(/^(?:_ga(?:_|$)|_gid$|_gat(?:_|$)|_gcl_)/);
  lsRemoveByPrefix("_gcl_");
}

/** Clear tracking data for categories the user has revoked. */
function clearRevokedData(state: ConsentState): void {
  clearRetiredGoogleData();
  if (!state.analytics) {
    window.mixpanel?.stop_session_recording?.();
    window.mixpanel?.opt_out_tracking?.({ delete_user: false });
    // Mixpanel localStorage data
    lsRemoveByPrefix("mp_");
  }
  if (!state.marketing) {
    // First-party affiliate cookie + localStorage keys (A7Y-1895).
    clearAffiliateState();
    // A7Y-2814: the session-scoped referral offer cache goes with it.
    clearReferralOffer();
  }
}

// ---------------------------------------------------------------------------
// Initialization (called once on app boot)
// ---------------------------------------------------------------------------

/**
 * Initialize consent on page load:
 * - If consent is NOT required (non-regulated region): load all scripts immediately.
 * - If consent IS required and user has previously accepted: load consented scripts.
 * - Otherwise: do nothing (banner will prompt the user).
 *
 * Returns true if the consent banner should be shown.
 */
export function initConsent(): boolean {
  clearRetiredGoogleData();
  if (!isConsentRequired()) {
    // Non-regulated region: load everything, no banner needed
    loadConsentedScripts({ analytics: true, marketing: true, updatedAt: "" });
    return false;
  }

  const existing = getConsentState();
  if (existing) {
    // User has previously made a choice — honour it
    loadConsentedScripts(existing);
    return false;
  }

  // No consent state — banner should be shown
  return true;
}
